import axios from 'axios';
import { SPEED_RANGES_CONFIGURATION } from '@/constants/global';
import { GroupDto } from '@/features/groups/group.dto';
import { UpdateFlag } from '@/features/multimedia/types/featureFlag.types';
import {
	createUserDto,
	DeliveryMethod,
	speedRangeConfigurationDto,
	UpdateMapConfigurations,
	updateUserDto,
	UserDto,
} from '@/features/users/users.dto';
import { MapConfigurations } from '@/OptimusRoutes/entities/mapConfigurations.entity';
import { UserInformation } from '@/OptimusRoutes/entities/userInformation.entity';
import { ApiResult, ServiceResponse } from '@/OptimusRoutes/interfaces';
import { ContactCodeVerification, ContactValidationResponse, ContactVerification } from './user.types';

type UserServiceType = {
	create: (payload: createUserDto) => Promise<ServiceResponse<UserDto>>;
	update: (payload: updateUserDto) => Promise<ServiceResponse<UserDto>>;
	findOne: (id: number) => Promise<ServiceResponse<UserDto>>;
	updateStatus: (id: number, status: boolean) => Promise<UserDto>;
	usernameExist: (username: string) => Promise<boolean>;
	emailExist: (email: string) => Promise<boolean>;
	getGroups: (id: number) => Promise<ServiceResponse<ApiResult<GroupDto[]>>>;
	sendEmailVerification: (id: number) => Promise<ServiceResponse>;
	confirmEmail: (id: number, token: string) => Promise<ServiceResponse<UserDto>>;
	getUserInformation: () => Promise<ServiceResponse<UserInformation>>;
	updateFlags: (id: number, flags: UpdateFlag) => Promise<ServiceResponse<{ featureFlags: string }>>;
	getSpeedRangeConfigurations: (id: number) => Promise<ServiceResponse<speedRangeConfigurationDto[]>>;
	createSpeedRangeConfigurations: () => Promise<ServiceResponse<speedRangeConfigurationDto[]>>;
	createOrEditSpeedRangeConfigurations: (
		items: speedRangeConfigurationDto[],
		itemsDeleted: speedRangeConfigurationDto[]
	) => Promise<ServiceResponse<speedRangeConfigurationDto[]>>;
	deletePlace: (userId: number, placeId: number) => Promise<ServiceResponse>;
	unsubscribeUserEmailFromAlerts: (emailId: string) => Promise<ServiceResponse>;
	updateMapConfiguration: (
		userId: number,
		data: UpdateMapConfigurations
	) => Promise<ServiceResponse<MapConfigurations>>;
	resetPassword: (id: number, deliveryMethod: DeliveryMethod) => Promise<ServiceResponse<string>>;
	sendContactVerificationCode: (verificationData: ContactVerification) => Promise<ServiceResponse>;
	verifyContactCode: (codeData: ContactCodeVerification) => Promise<ServiceResponse<ContactValidationResponse>>;
	deleteAllSessions: (userId: number) => Promise<ServiceResponse>;
};

const create = async (payload: createUserDto): Promise<ServiceResponse<UserDto>> => {
	const { data } = await axios.post<ServiceResponse<UserDto>>(`/v2/users`, payload);
	return data;
};

const findOne = async (id: number): Promise<ServiceResponse<UserDto>> => {
	const { data } = await axios.get<ServiceResponse<UserDto>>(`/v2/users/${id}`);
	return data;
};

const update = async ({ id, ...payload }: updateUserDto): Promise<ServiceResponse<UserDto>> => {
	const { data } = await axios.patch<ServiceResponse<UserDto>>(`/v2/users/${id}`, payload);
	return data;
};

const updateStatus = async (id: number, status: boolean): Promise<UserDto> =>
	axios.patch(`/v2/users/${id}/status`, { status });

const usernameExist = async (username: string): Promise<boolean> => {
	try {
		const { data } = await axios.get(`/v2/users/exists?username=${encodeURIComponent(username)}`);

		if (data?.error) {
			return false;
		}

		return data.success;
	} catch (error) {
		return false;
	}
};

const emailExist = async (email: string): Promise<boolean> => {
	try {
		const { data } = await axios.get(`/v2/users/exists?email=${encodeURIComponent(email)}`);

		if (data?.error) {
			return false;
		}

		return data.success;
	} catch (error) {
		return false;
	}
};

const getGroups = async (id: number): Promise<ServiceResponse<ApiResult<GroupDto[]>>> => {
	const { data } = await axios.get<ServiceResponse<ApiResult<GroupDto[]>>>(`/v2/users/${id}/groups`);
	return data;
};

const sendEmailVerification = async (id: number): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/users/${id}/sendEmailVerification`);
	return data;
};

const getUserInformation = async (): Promise<ServiceResponse<UserInformation>> => {
	const { data } = await axios.get<ServiceResponse<UserInformation>>(`/v2/users/me`);
	return data;
};

const updateFlags = async (
	id: number,
	featureFlags: UpdateFlag
): Promise<ServiceResponse<{ featureFlags: string }>> => {
	const { data } = await axios.patch<ServiceResponse<{ featureFlags: string }>>(
		`/v2/users/${id}/feature-flags`,
		featureFlags
	);
	return data;
};

const getSpeedRangeConfigurations = async (id: number): Promise<ServiceResponse<speedRangeConfigurationDto[]>> => {
	const { data } = await axios.get<ServiceResponse<speedRangeConfigurationDto[]>>(
		`/v2/users/${id}/speed-range-configurations`
	);
	return data;
};
const createSpeedRangeConfigurations = async (): Promise<ServiceResponse<speedRangeConfigurationDto[]>> => {
	const { data } = await axios.post<ServiceResponse<speedRangeConfigurationDto[]>>(
		`/v2/users/speed-range-configurations/add-configurations`,
		{
			items: SPEED_RANGES_CONFIGURATION,
		}
	);
	return data;
};
const createOrEditSpeedRangeConfigurations = async (
	items: speedRangeConfigurationDto[],
	itemsDeleted: speedRangeConfigurationDto[]
): Promise<ServiceResponse<speedRangeConfigurationDto[]>> => {
	const { data } = await axios.post<ServiceResponse<speedRangeConfigurationDto[]>>(
		`/v2/users/speed-range-configurations/add-or-edit-configurations`,
		{
			items,
			itemsDeleted,
		}
	);
	return data;
};

const confirmEmail = async (id: number, token: string): Promise<ServiceResponse<UserDto>> => {
	const { data } = await axios.post<ServiceResponse<UserDto>>(`/v2/users/${id}/verify-account`, { token });
	return data;
};

const deletePlace = async (userId: number, placeId: number): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/users/${userId}/places/${placeId}`);
	return data;
};

const unsubscribeUserEmailFromAlerts = async (emailId: string): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>('/v2/users/unsubscribe-from-all-alerts', {
		data: { emailId },
	});
	return data;
};

const updateMapConfiguration = async (
	id: number,
	mapData: UpdateMapConfigurations
): Promise<ServiceResponse<MapConfigurations>> => {
	const { data } = await axios.patch<ServiceResponse<MapConfigurations>>(
		`/v2/users/${id}/map-configurations`,
		mapData
	);
	return data;
};

const resetPassword = async (id: number, deliveryMethod: DeliveryMethod): Promise<ServiceResponse<string>> => {
	const { data } = await axios.post<ServiceResponse<string>>(`/v2/users/${id}/reset-password`, { deliveryMethod });
	return data;
};

const sendContactVerificationCode = async (verificationData: ContactVerification): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>('/v2/users/send-verification-code', verificationData);
	return data;
};

export const verifyContactCode = async (
	codeData: ContactCodeVerification
): Promise<ServiceResponse<ContactValidationResponse>> => {
	const { data } = await axios.post<ServiceResponse<ContactValidationResponse>>('/v2/users/verify-code', codeData);
	return data;
};

const deleteAllSessions = async (userId: number): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/users/${userId}/sessions`);
	return data;
};

const UserService: UserServiceType = {
	create,
	update,
	findOne,
	updateStatus,
	usernameExist,
	emailExist,
	getGroups,
	sendEmailVerification,
	getUserInformation,
	updateFlags,
	getSpeedRangeConfigurations,
	createSpeedRangeConfigurations,
	createOrEditSpeedRangeConfigurations,
	confirmEmail,
	deletePlace,
	unsubscribeUserEmailFromAlerts,
	updateMapConfiguration,
	resetPassword,
	sendContactVerificationCode,
	verifyContactCode,
	deleteAllSessions,
};

export default UserService;
